import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MetricCard, type MetricStatus } from './MetricCard';
import { ChangeFeedPanel, type ChangeFeedItem } from './ChangeFeedPanel';
import { PinnedChartsGrid } from './PinnedChartsGrid';
import { ChartCard } from '@/components/visuals/ChartCard';
import { CHART_REGISTRY, type ChartDefinition } from '@/pages/analytics/overview/registry';
import { toActivitiesUrl, toAnalyticsUrl } from '@/components/visuals';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CockpitData {
  /** Float distribution buckets */
  floatDist: Array<{ bucket: string; count: number }>;
  /** Critical / near-critical / monitor counts */
  criticalCount: number;
  nearCriticalCount: number;
  monitorCount: number;
  /** Total schedule activities (non-null merged data) */
  totalActivities: number;
  /** Quality check rows */
  qualityChecks: Array<{ id: string; label: string; value: number; status: string }>;
  /** Milestones with variance */
  milestonesWithVariance: Array<{ name: string; varianceDays: number | null }>;
  /** Whether baseline data is available */
  hasBaselineData: boolean;
  /** WBS hotspot data */
  wbsHotspots: Array<{ wbs_id: string; wbs_short_name: string; wbs_name?: string; hotspotScore: number }>;
  /** True when the data fetch returned null (no schedule loaded) */
  hasData: boolean;
}

export interface ChartCockpitProps {
  data: CockpitData;
  /** When true, change feed cannot show deltas (e.g. fewer than 2 snapshots). */
  singleSnapshot?: boolean;
  pinnedIds: string[];
  renderChart: (chartId: string, height: number | string) => React.ReactNode;
  onSelectChart: (chartId: string) => void;
  onTogglePin: (chartId: string) => void;
  onInfoClick?: (chart: ChartDefinition) => void;
}

// ---------------------------------------------------------------------------
// KPI helpers
// ---------------------------------------------------------------------------

function negativeFloatCount(floatDist: CockpitData['floatDist']): number | null {
  const bucket = floatDist.find((d) => d.bucket === '<0');
  return bucket ? bucket.count : null;
}

function totalFloatActivities(floatDist: CockpitData['floatDist']): number {
  return floatDist.reduce((sum, d) => sum + d.count, 0);
}

function totalQualityIssues(qualityChecks: CockpitData['qualityChecks']): number {
  return qualityChecks.reduce((sum, q) => sum + q.value, 0);
}

function worstMilestoneVariance(milestones: CockpitData['milestonesWithVariance']): number | null {
  if (milestones.length === 0) return null;
  return Math.max(...milestones.map((m) => m.varianceDays ?? 0));
}

function wbsAtRiskCount(hotspots: CockpitData['wbsHotspots']): number {
  return hotspots.filter((w) => w.hotspotScore > 0).length;
}

function resolveStatus(value: number | null, warn: number, crit: number): MetricStatus {
  if (value === null) return 'unavailable';
  if (value <= 0) return 'ok';
  if (value < crit) return 'warning';
  return 'critical';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ChartCockpit: React.FC<ChartCockpitProps> = ({
  data,
  singleSnapshot: singleSnapshotProp,
  pinnedIds,
  renderChart,
  onSelectChart,
  onTogglePin,
  onInfoClick,
}) => {
  const navigate = useNavigate();

  // KPI strip values
  const negFloat = data.hasData ? negativeFloatCount(data.floatDist) : null;
  const totalActs = data.hasData ? totalFloatActivities(data.floatDist) : null;
  const totalQuality = data.hasData && data.qualityChecks.length > 0 ? totalQualityIssues(data.qualityChecks) : null;
  const worstVariance = data.hasBaselineData ? worstMilestoneVariance(data.milestonesWithVariance) : null;
  const riskWbs = data.hasData && data.wbsHotspots.length > 0 ? wbsAtRiskCount(data.wbsHotspots) : null;

  // Change feed: single snapshot when not provided (default) or when trends indicate < 2 snapshots
  const singleSnapshot = singleSnapshotProp ?? true;
  const changeFeedItems: ChangeFeedItem[] = useMemo(() => {
    if (singleSnapshot || !data.hasData) return [];
    // Placeholder: would be populated from diff engine
    return [];
  }, [singleSnapshot, data.hasData]);

  // Recommended charts (not pinned, by priority, max 3)
  const recommendedCharts = useMemo(() => {
    const pinnedSet = new Set(pinnedIds);
    return [...CHART_REGISTRY]
      .filter((c) => !pinnedSet.has(c.id))
      .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
      .slice(0, 3);
  }, [pinnedIds]);

  const handleViewAllPinned = () => {
    navigate('/app/analytics/catalog?pinned=true');
  };

  return (
    <div className="flex flex-col gap-6">
      {/* -------- A) KPI strip -------- */}
      <section>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <MetricCard
            label="Total activities"
            value={totalActs}
            status="neutral"
            onClick={() => navigate('/app/activities')}
          />
          <MetricCard
            label="Negative float"
            value={negFloat}
            detail={negFloat !== null && totalActs ? `of ${totalActs} total` : undefined}
            status={resolveStatus(negFloat, 1, 6)}
            onClick={() => navigate(toActivitiesUrl({ negativeFloat: true }))}
          />
          <MetricCard
            label="Critical activities"
            value={data.hasData ? data.criticalCount : null}
            detail={data.hasData ? `Near: ${data.nearCriticalCount} · Monitor: ${data.monitorCount}` : undefined}
            status={resolveStatus(data.criticalCount, 1, 26)}
            onClick={() => navigate(toAnalyticsUrl('critical-path'))}
          />
          <MetricCard
            label="Quality issues"
            value={totalQuality}
            status={totalQuality === null ? 'unavailable' : totalQuality === 0 ? 'ok' : totalQuality <= 10 ? 'warning' : 'critical'}
            onClick={() => navigate(toAnalyticsUrl('quality'))}
          />
          <MetricCard
            label="Worst milestone variance"
            value={worstVariance !== null ? `+${worstVariance}d` : null}
            status={resolveStatus(worstVariance, 1, 15)}
            onClick={() => navigate(toAnalyticsUrl('milestones'))}
          />
          <MetricCard
            label="WBS at risk"
            value={riskWbs}
            detail={riskWbs !== null ? `of ${data.wbsHotspots.length} WBS nodes` : undefined}
            status={riskWbs === null ? 'unavailable' : riskWbs === 0 ? 'ok' : riskWbs <= 3 ? 'warning' : 'critical'}
            onClick={() => navigate(toAnalyticsUrl('overview') + '?view=focus&chart=wbs_hotspots')}
          />
        </div>
      </section>

      {/* -------- B) What changed -------- */}
      <section>
        <ChangeFeedPanel
          items={changeFeedItems}
          onNavigate={(href) => navigate(href)}
          singleSnapshot={singleSnapshot}
        />
      </section>

      {/* -------- C) Pinned charts (max 6) -------- */}
      <section>
        <h3 className="text-sm font-medium text-foreground mb-3">Pinned</h3>
        <PinnedChartsGrid
          charts={CHART_REGISTRY}
          pinnedIds={pinnedIds}
          renderChart={renderChart}
          onSelectChart={onSelectChart}
          onTogglePin={onTogglePin}
          onInfoClick={onInfoClick}
          onViewAllPinned={handleViewAllPinned}
        />
      </section>

      {/* -------- D) Recommended (max 3) -------- */}
      {recommendedCharts.length > 0 && (
        <section>
          <h3 className="text-sm font-medium text-foreground mb-3">Recommended</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {recommendedCharts.map((chart) => (
              <ChartCard
                key={chart.id}
                title={chart.title}
                subtitle={chart.description}
                variant="compact"
                compactChartHeight={chart.compactHeight}
                onClick={() => onSelectChart(chart.id)}
                onInfoClick={onInfoClick ? () => onInfoClick(chart) : undefined}
                actions={
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onTogglePin(chart.id);
                    }}
                    className="shrink-0 px-2 py-0.5 text-xs rounded border text-muted-foreground border-border hover:bg-muted hover:text-foreground transition-colors"
                    title="Pin to Overview"
                  >
                    Pin
                  </button>
                }
              >
                {renderChart(chart.id, '100%')}
              </ChartCard>
            ))}
          </div>
        </section>
      )}

      {/* -------- E) Browse CTA -------- */}
      <section className="flex items-center justify-center py-4">
        <button
          type="button"
          onClick={() => navigate('/app/analytics/catalog')}
          className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Browse full library
        </button>
      </section>
    </div>
  );
};
